import styled from 'foundations-theming/utils';
import { Component } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import debounce from 'transmute/debounce';
import PropTypes from 'prop-types';
import { fromJS, Map as ImmutableMap } from 'immutable';
import partial from 'react-utils/partial';
import { LinkIcon } from 'foundations-assets';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Overlay } from 'foundations-components/transitional/components/Overlay';
import { OptimisticNanoProgress } from 'foundations-components/transitional/components/OptimisticNanoProgress';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import ImageLinkPopover from './components/ImageLinkPopover';
import { getFileInfo } from './api/FileManagerApi';
import { getSignedSrc } from './utils/Images';
import ResizeableThumbnailDragHandleCorners from './components/ResizeableThumbnailDragHandleCorners';
import ResizeableThumbnailOutline from './components/ResizeableThumbnailOutline';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { View } from 'foundations-components/components/View';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const isLegacySignedUrl = url => url && url.includes('rich_text_hidden_files');
const getLegacySignedUrlBasePath = signedUrl => {
  const pathStartIndex = signedUrl.indexOf('rich_text_hidden_files');
  const pathEndIndex = signedUrl.indexOf('?Expires');
  return signedUrl.slice(pathStartIndex, pathEndIndex);
};
const ImageLinkButton = props => {
  return /*#__PURE__*/_jsx("span", {
    onClick: props.togglePopover,
    children: /*#__PURE__*/_jsx(LinkIcon, {})
  });
};
ImageLinkButton.propTypes = {
  togglePopover: PropTypes.func
};
const ResizeableThumbnailPreview = styled.img.withConfig({
  displayName: "ImageComponent__ResizeableThumbnailPreview",
  componentId: "ybxk1z-0"
})(["block-size:", "px;inline-size:", "px;opacity:0.3;position:absolute;top:-3px;left:-3px;display:", ";"], props => props.height, props => props.width, props => props.display === false ? 'none' : 'block');
const ImageWrapper = styled.div.withConfig({
  displayName: "ImageComponent__ImageWrapper",
  componentId: "ybxk1z-1"
})(["display:inline-flex;position:relative;"]);
const DecoratedLinkButton = ImageLinkPopover(ImageLinkButton);
class ImageComponent extends Component {
  constructor(props) {
    super(props);
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'draftContext' does not exist on type 'Im... Remove this comment to see the full error message
    this.debouncedUpdateData = debounce(300, (...args) => {
      const {
        useProsemirror,
        prosemirrorProps
      } = this.props;
      if (useProsemirror) {
        prosemirrorProps.updateImage(...args);
      } else {
        // @ts-expect-error ts-migrate(2556) FIXME: A spread argument must either have a tuple type or... Remove this comment to see the full error message
        this.updateData(...args);
      }
    });
    this.handleClick = () => {
      if (this.state.isInitiallyReadOnly) {
        return;
      }
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);
      this.props.onFocus();
      this.ensureValidDimensions();
    };
    this.handleBlur = () => {
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);
      this.handleLinkPopoverClose();
      this.linkPopover.clearInput();
    };
    this.updateData = data => {
      const {
        block
      } = this.props;
      const {
        getEditorState,
        onChange
      } = this.props.draftContext;
      const editorState = getEditorState();
      const {
        image = {},
        link = {}
      } = data;
      const oldData = this.getData();
      const updatedBlockData = oldData.mergeDeep({
        image,
        link
      });

      // @ts-expect-error ts-migrate(2304) FIXME: Cannot find name 'changeBlockData'.
      const updatedEditorState = changeBlockData({
        editorState,
        block,
        updatedBlockData
      });
      onChange(updatedEditorState);
    };
    this.getData = () => {
      const blockData = this.props.block.getData();
      const isFormatted = ImmutableMap.isMap(blockData.get('image'));
      if (isFormatted) {
        return blockData;
      }
      const rawData = blockData.toObject();
      // the `convertFromRaw` function supplied by draft-js does not
      // deeply convert block data to Immutable objects (it simply
      // calls the Map constructor on `block.data`), so we do this
      // hack to make sure all our data is in Immutable form
      const deeplyConverted = fromJS(rawData);
      return deeplyConverted;
    };
    this.updateSize = (width, height) => {
      if (width > 0 && height > 0) {
        const newData = {
          image: {
            width,
            height
          }
        };
        this.debouncedUpdateData(newData);
        this.setState({
          width: Math.round(width),
          height: Math.round(height),
          previewWidth: null,
          previewHeight: null
        });
      }
    };
    this.ensureValidDimensions = () => {
      const {
        width,
        height
      } = this.state;
      if (!width || !height) {
        const parentRect = this.parent.getBoundingClientRect();
        const parentWidth = parentRect.width;
        const parentHeight = parentRect.height;
        this.setState({
          ratio: parentWidth / parentHeight
        });
        this.updateSize(parentWidth, parentHeight);
      }
    };
    this.getImageRatio = () => {
      const {
        width,
        height
      } = this.img.getBoundingClientRect();
      const ratio = width / height;
      this.setState({
        ratio,
        isPct: false
      });
      return ratio;
    };
    this.getNewBounds = e => {
      const {
        isPct,
        originalDragPosition,
        width,
        height,
        invertHorizontal,
        invertVertical
      } = this.state;
      let {
        ratio
      } = this.state;
      if (isPct) {
        ratio = this.getImageRatio();
      }
      const xDiff = originalDragPosition.clientX - e.clientX;
      const yDiff = originalDragPosition.clientY - e.clientY;

      //Calculate the new bounds for the image for both the X and Y distance dragged
      const newWidthX = invertHorizontal ? width + xDiff : width - xDiff;
      const newHeightX = Math.round(newWidthX / ratio);
      const newHeightY = invertVertical ? height + yDiff : height - yDiff;
      const newWidthY = Math.round(newHeightY * ratio);
      if (newWidthX > newWidthY) {
        return {
          newWidth: newWidthX,
          newHeight: newHeightX
        };
      } else {
        return {
          newWidth: newWidthY,
          newHeight: newHeightY
        };
      }
    };
    this.handleDefault = e => {
      e.preventDefault();
      if (e.type === 'click') {
        e.stopPropagation();
      }
    };
    this.handleMouseDown = (e, invertHorizontal, invertVertical) => {
      const {
        width,
        height
      } = this.state;
      this.setState({
        dragging: true,
        originalDragPosition: {
          clientX: e.clientX,
          clientY: e.clientY
        },
        invertHorizontal,
        invertVertical,
        previewWidth: width,
        previewHeight: height,
        mouseDraggingX: e.pageX
      });
      this.handleDefault(e);
    };
    this.handleMouseUp = e => {
      const {
        dragging
      } = this.state;
      if (dragging) {
        const {
          newWidth,
          newHeight
        } = this.getNewBounds(e);
        this.updateSize(newWidth, newHeight);
      }
      this.setState({
        dragging: false,
        originalDragPosition: null,
        invertHorizontal: null,
        invertVertical: null,
        previewWidth: null,
        previewHeight: null,
        mouseDraggingX: null
      });
      this.handleDefault(e);
    };
    this.handleMouseMove = e => {
      const {
        dragging
      } = this.state;
      if (dragging) {
        const {
          newWidth,
          newHeight
        } = this.getNewBounds(e);
        this.setState({
          previewWidth: newWidth,
          previewHeight: newHeight,
          mouseDraggingX: e.pageX
        });
      }
    };
    this.handleButtonClick = (newWidth, e) => {
      const {
        isPct
      } = this.state;
      let {
        ratio
      } = this.state;
      this.handleDefault(e);
      if (isPct) {
        ratio = this.getImageRatio();
      }
      const newHeight = Math.round(newWidth / ratio);
      this.updateSize(newWidth, newHeight);
      this.setState({
        dragging: false,
        originalDragPosition: null,
        invertHorizontal: null,
        invertVertical: null
      });
    };
    this.handleLinkPopoverOpen = () => {
      this.props.overrideKeyDown();
      this.setState({
        linkPopoverOpen: true
      });
    };
    this.handleLinkPopoverClose = () => {
      this.props.restoreKeyDown();
      this.setState({
        linkPopoverOpen: false
      });
    };
    this.handleLinkPopoverOpenChange = e => {
      this.setState({
        linkPopoverOpen: e.target.value
      });
    };
    this.handleLinkChange = newData => {
      this.debouncedUpdateData({
        link: newData
      });
      this.setState(Object.assign({
        linkPopoverOpen: false
      }, newData));
      this.props.onFocus();
    };
    this.handleRemove = () => {
      this.handleLinkChange({
        url: null,
        isTargetBlank: null
      });
    };
    this.renderResizeTools = previewImgSrc => {
      var _this$props$prosemirr;
      const {
        selected
      } = this.props;
      const {
        width,
        height,
        dragging,
        isPct,
        previewWidth,
        previewHeight,
        invertHorizontal,
        invertVertical,
        mouseDraggingX
      } = this.state;
      const shouldDisplayBadgeToLeft = ((_this$props$prosemirr = this.props.prosemirrorProps) === null || _this$props$prosemirr === void 0 ? void 0 : _this$props$prosemirr.align) === 'right';
      const left = mouseDraggingX;
      const right = !left && shouldDisplayBadgeToLeft ? width : undefined;
      return /*#__PURE__*/_jsxs(View, Object.assign({}, isPct ? {
        inlineSize: width,
        blockSize: height,
        position: 'absolute'
      } : {}, {
        children: [/*#__PURE__*/_jsxs(ResizeableThumbnailOutline
        // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
        , {
          height: previewHeight || height,
          width: previewWidth || width,
          invertHorizontal: invertHorizontal,
          invertVertical: invertVertical,
          selected: selected,
          children: [/*#__PURE__*/_jsx(ResizeableThumbnailDragHandleCorners, {
            selected: selected,
            handleMouseDown: this.handleMouseDown
          }), /*#__PURE__*/_jsx(ResizeableThumbnailPreview, {
            src: previewImgSrc,
            height: previewHeight || height,
            width: previewWidth || width
            // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
            ,
            display: dragging === false ? 'none' : 'block'
          })]
        }), /*#__PURE__*/_jsx(Badge, {
          use: "pantera",
          position: "absolute",
          margin: "200",
          insetInlineStart: left,
          insetInlineEnd: right,
          children: `${previewWidth || width} x ${previewHeight || height}`
        })]
      }));
    };
    this.renderProgressOverlay = (isTemporary, uploadPercent) => {
      if (!isTemporary || uploadPercent >= 100) {
        return null;
      }
      const {
        width
      } = this.state;
      return /*#__PURE__*/_jsxs(Overlay, {
        contextual: true,
        width: width,
        use: "light",
        children: [/*#__PURE__*/_jsx(OptimisticNanoProgress, {
          value: uploadPercent
        }), /*#__PURE__*/_jsx(TFlex, {
          alignItems: "center",
          justifyContent: "center",
          inset: "0",
          position: "absolute",
          children: /*#__PURE__*/_jsx(LoadingSpinner, {})
        })]
      });
    };
    const {
      useProsemirror: _useProsemirror,
      prosemirrorProps: _prosemirrorProps,
      draftContext
    } = props;
    const isInitiallyReadOnly = _useProsemirror ? false : draftContext.getReadOnly();
    let _width;
    let _height;
    if (_useProsemirror) {
      _width = _prosemirrorProps.width;
      _height = _prosemirrorProps.height;
    } else {
      const {
        image
      } = this.getData().toObject();
      _width = image.toObject().width;
      _height = image.toObject().height;
    }
    const _isPct = _width && typeof _width === 'string' && _width.indexOf('%') !== -1;
    let parsedWidth;
    let parsedHeight;
    let _ratio;
    if (_isPct) {
      parsedWidth = _width;
      parsedHeight = _height;
    } else {
      parsedWidth = parseInt(_width, 10);
      parsedHeight = parseInt(_height, 10);
      if (!isNaN(parsedWidth) && !isNaN(parsedHeight)) {
        _ratio = parsedWidth / parsedHeight;
      }
    }
    this.state = {
      ratio: _ratio,
      isPct: _isPct,
      isInitiallyReadOnly,
      width: parsedWidth,
      height: parsedHeight,
      dragging: false,
      linkPopoverOpen: false
    };
  }
  componentDidMount() {
    const {
      useProsemirror,
      prosemirrorProps
    } = this.props;
    const src = useProsemirror ? prosemirrorProps.src : this.getData().getIn(['image', 'src']);
    if (isLegacySignedUrl(src)) {
      const fileManagerPath = getLegacySignedUrlBasePath(src);
      getFileInfo(fileManagerPath).then(({
        id
      }) => {
        const newSrc = getSignedSrc(id);
        const updatedData = {
          image: {
            src: newSrc
          }
        };
        if (useProsemirror) {
          prosemirrorProps.updateImage(updatedData);
        } else {
          this.updateData(updatedData);
        }
      }).catch(e => {
        console.error(e);
      });
    }
  }
  componentDidUpdate(prevProps) {
    if (prevProps.selected === true && this.props.selected === false) {
      this.handleBlur();
    }
  }
  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  }

  // This function gets the new bounds (width and height) of an image given
  // the original bounds, where you started dragging from, where you
  // finished dragging (gotten from the event), the ratio of the image, and
  // if we should invert the horizontal and the vertical dimensions.
  // the Invert variables are set based on which corner you are dragging from
  // (Values found in the ResizeableThumbnailDragHandleCorners file)
  // Top Left: Both Inverted
  // Top Right: Horizontal Inverted
  // Bottom Left: Vertical Inverted
  // Bottom Right: Neither Inverted
  render() {
    const {
      useProsemirror,
      prosemirrorProps,
      selected
    } = this.props;
    const {
      width,
      height,
      isPct,
      linkPopoverOpen,
      dragging
    } = this.state;
    const {
      image,
      link
    } = useProsemirror ? {} : this.getData().toObject();
    let url;
    let isTargetBlank;
    let isNoFollow;
    if (link) {
      url = link.get('url');
      isTargetBlank = link.get('isTargetBlank');
      isNoFollow = link.get('isNoFollow');
    }
    if (useProsemirror) {
      url = prosemirrorProps.link.url;
      isTargetBlank = prosemirrorProps.link.isTargetBlank;
      isNoFollow = prosemirrorProps.link.isNoFollow;
    }
    const align = prosemirrorProps === null || prosemirrorProps === void 0 ? void 0 : prosemirrorProps.align;
    const src = useProsemirror ? prosemirrorProps.src : this.getData().getIn(['image', 'src']);
    const isTemporary = useProsemirror ? !!prosemirrorProps.isTemporary : !!this.getData().getIn(['image', 'isTemporary']);
    const uploadPercent = useProsemirror ? prosemirrorProps.uploadPercent : this.getData().getIn(['image', 'uploadPercent']);
    const sizes = [250, 400];
    let buttons = [];
    const originalDimensions = useProsemirror ? prosemirrorProps.originalDimensions : image.get('originalDimensions');
    if (originalDimensions && originalDimensions.width) {
      buttons.push( /*#__PURE__*/_jsx(Link, {
        disabled: originalDimensions.width === width,
        onClick: partial(this.handleButtonClick, originalDimensions.width),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "draftPlugins.imagePlugin.resetSize"
        })
      }, "image-resize-button-original"));
    }
    buttons = buttons.concat(sizes.map(size => {
      return /*#__PURE__*/_jsx(Link, {
        disabled: size === width,
        onClick: partial(this.handleButtonClick, size),
        children: `${size}px`
      }, `image-resize-button-${size}`);
    }));
    buttons.push( /*#__PURE__*/_jsx(Link, {
      children: /*#__PURE__*/_jsx(DecoratedLinkButton, {
        ref: c => {
          this.linkPopover = c;
          return c;
        },
        open: linkPopoverOpen,
        onOpenChange: this.handleLinkPopoverOpenChange,
        url: url,
        isTargetBlank: isTargetBlank,
        isNoFollow: isNoFollow,
        onOpen: this.handleLinkPopoverOpen,
        onCancel: this.handleLinkPopoverClose,
        onConfirm: this.handleLinkChange,
        onRemove: this.handleRemove
      })
    }, "image-link-button"));
    return /*#__PURE__*/_jsx(View, {
      textAlign: align,
      ref: this.props.forwardRef,
      children: /*#__PURE__*/_jsxs(ImageWrapper, {
        ref: c => {
          this.parent = c;
          return c;
        },
        onClick: this.handleClick,
        children: [this.renderProgressOverlay(isTemporary, uploadPercent), /*#__PURE__*/_jsx(Tooltip, {
          open: selected && !dragging,
          use: "longform",
          paddingBlock: "200",
          paddingInline: "300",
          title: /*#__PURE__*/_jsx(List, {
            inline: true,
            use: "inline-divided",
            padding: "0",
            children: buttons.map(button => /*#__PURE__*/_jsx(ListItem, {
              children: button
            }, button.key))
          }),
          children: /*#__PURE__*/_jsx("img", Object.assign({
            ref: c => {
              this.img = c;
              return c;
            },
            src: src
          }, isPct ? {
            style: {
              width,
              height
            }
          } : {
            width,
            height
          }))
        }), this.renderResizeTools(src)]
      })
    });
  }
}
ImageComponent.displayName = 'ImageComponent';
ImageComponent.propTypes = {
  block: PropTypes.object,
  selected: PropTypes.bool.isRequired,
  onFocus: PropTypes.func.isRequired,
  overrideKeyDown: PropTypes.func.isRequired,
  restoreKeyDown: PropTypes.func.isRequired,
  forwardRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  useProsemirror: PropTypes.bool,
  prosemirrorProps: PropTypes.object
};
export const ProsemirrorImageComponent = ImageComponent;